import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { EmployeeSearchFilter } from "../features/employees/employees.types";
import { ProjectTimeLogFilterDto } from "../features/project-time-log/project-time-log.types";
import { DefaultPageSizes } from "./constants";
import { RootState } from "./store";
import { TransactionFilter } from "../features/transaction/txns.types";
import { getCachedDateRange } from "../components/SmartDateRangeSelector";
import { TenantInvoiceFilter } from "../features/tenant/tenant.types";
import { ReferralCodesFilter, ReferralCodeTransactionsFilter } from "../features/referral/referral.types";
import { BranchFilterDto } from "../features/branch/branch.types";
import { TeamFilterDto } from "../features/team/team.types";
import { WorkLogFilter } from "../features/worklog/worklog.types";
import { ProjectFilter } from "../features/project/project.types";
import { DepartmentFilterDto } from "../features/department/department.types";

export const SMART_DATE_FILTER_KEYS = {
    TRANSACTIONS: "transaction_date_filter",
    WORKLOG_LIST: "worklog_date_filter_list_filter",
    WORKLOG_SUMMARY: "worklog_date_filter_summary_filter",
    PROJECT_TIME_LOGS: "project_time_logs",
};

const dateRange = getCachedDateRange(SMART_DATE_FILTER_KEYS.TRANSACTIONS);
const workLogListDateRange = getCachedDateRange(SMART_DATE_FILTER_KEYS.WORKLOG_LIST, "this_month");
const workLogSummaryDateRange = getCachedDateRange(SMART_DATE_FILTER_KEYS.WORKLOG_SUMMARY, "this_month");
const projectTimeLogsDateRange = getCachedDateRange(SMART_DATE_FILTER_KEYS.PROJECT_TIME_LOGS, "this_month");

export const FILTER_MODULES = {
    EMPLOYEES: "employees",
    TRANSACTIONS: "transactions",
    TENANT_INVOICE: "tenantInvoice",
    REFERRAL_CODES: "referralCodes",
    REFERRAL_CODE_TRANSACTIONS: "referralCodeTransactions",
    BRANCHES: "branches",
    TEAMS: "teams",
    DEPARTMENTS: "departments",
    WORKLOGS: "worklogs",
    SELF_WORKLOGS: "selfWorklogs",
    WORKLOG_SUMMARY: "worklogSummary",
    PROJECTS: "projects",
    PROJECT_TIME_LOGS: "projectTimeLogs",
} as const;

export interface SearchFiltersState {
    employees: EmployeeSearchFilter;
    transactions: TransactionFilter;
    tenantInvoice: TenantInvoiceFilter;
    referralCodes: ReferralCodesFilter;
    referralCodeTransactions: ReferralCodeTransactionsFilter;
    branches: BranchFilterDto;
    teams: TeamFilterDto;
    departments: DepartmentFilterDto;
    worklogs: WorkLogFilter;
    selfWorklogs: WorkLogFilter;
    worklogSummary: WorkLogFilter;
    projects: ProjectFilter;
    projectTimeLogs: ProjectTimeLogFilterDto;
}

const initialState: SearchFiltersState = {
    employees: {
        showInactive: false,
        page: 1,
        pageSize: DefaultPageSizes.Employees,
    },
    transactions: {
        from: dateRange?.from,
        to: dateRange?.to,
        projects: [],
        page: 1,
        pageSize: DefaultPageSizes.Transactions,
    },
    tenantInvoice: {
        from: undefined,
        to: undefined,
        overdueOnly: false,
        page: 1,
        pageSize: DefaultPageSizes.md,
    },
    referralCodes: {
        searchTerm: "",
        userId: undefined,
        includeInactive: false,
        page: 1,
        pageSize: DefaultPageSizes.md,
    },
    referralCodeTransactions: {
        referralCodeId: undefined,
        page: 1,
        pageSize: DefaultPageSizes.md,
    },
    branches: {
        searchTerm: "",
        page: 1,
        pageSize: DefaultPageSizes.md,
    },
    teams: {
        searchTerm: "",
    },
    departments: {
        searchTerm: "",
    },
    worklogs: {
        from: workLogListDateRange?.from,
        to: workLogListDateRange?.to,
        page: 1,
        pageSize: DefaultPageSizes.WorkLogs,
    },
    selfWorklogs: {
        page: 1,
        pageSize: DefaultPageSizes.WorkLogs,
    },
    worklogSummary: {
        from: workLogSummaryDateRange?.from,
        to: workLogSummaryDateRange?.to,
        page: 1,
        pageSize: DefaultPageSizes.WorkLogs,
    },
    projects: {
        showInactiveProjects: false,
        searchTerm: "",
    },
    projectTimeLogs: {
        from: projectTimeLogsDateRange?.from,
        to: projectTimeLogsDateRange?.to,
    },
};

export const searchFiltersSlice = createSlice({
    name: "searchFilters",
    initialState,
    reducers: {
        changeFilter: (state, action: PayloadAction<{ module: keyof SearchFiltersState; filter: any }>) => {
            const { module, filter } = action.payload;
            if (module in state) {
                (state[module] as any) = {
                    ...state[module],
                    ...filter,
                };
            }
        },
        clearFilters: (state, action: PayloadAction<keyof SearchFiltersState>) => {
            const module = action.payload;
            if (state[module] && initialState[module]) {
                (state as any)[module] = { ...initialState[module] };
            }
        },
    },
});

export default searchFiltersSlice.reducer;

export const { changeFilter, clearFilters } = searchFiltersSlice.actions;

export const employeeFilterSelector = (state: RootState) => state.searchFilters.employees;
export const transactionFilterSelector = (state: RootState) => state.searchFilters.transactions;
export const tenantInvoiceFilterSelector = (state: RootState) => state.searchFilters.tenantInvoice;
export const referralCodesFilterSelector = (state: RootState) => state.searchFilters.referralCodes;
export const referralCodeTransactionsFilterSelector = (state: RootState) =>
    state.searchFilters.referralCodeTransactions;
export const branchFilterSelector = (state: RootState) => state.searchFilters.branches;
export const teamFilterSelector = (state: RootState) => state.searchFilters.teams;
export const departmentFilterSelector = (state: RootState) => state.searchFilters.departments;
export const worklogFilterSelector = (state: RootState) => state.searchFilters.worklogs;
export const selfWorklogFilterSelector = (state: RootState) => state.searchFilters.selfWorklogs;
export const worklogSummaryFilterSelector = (state: RootState) => state.searchFilters.worklogSummary;
export const projectFilterSelector = (state: RootState) => state.searchFilters.projects;
export const projectTimeLogsFilterSelector = (state: RootState) => state.searchFilters.projectTimeLogs;
